import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../Components/AuthContext';
import hasPermission from '../../Hooks/hasPermission';

/**
 * Envoltura de ruta que exige uno o varios permisos.
 *
 * ## Qué resuelve y qué NO
 *
 * Resuelve la **experiencia**: quien escribe una URL que no le toca aterriza en el
 * dashboard en lugar de en una pantalla que va a responder 403 en cada petición y
 * se ve como si estuviera rota.
 *
 * 🔴 **No es seguridad.** Esto corre en el navegador y se puede saltar. La
 * protección real es `authorize([...])` en el backend, endpoint por endpoint. Si un
 * dato no debe verse, el que tiene que negarlo es el servidor — esta envoltura solo
 * evita el desorden.
 *
 * ## Por qué espera a que cargue la sesión
 *
 * `permissionsActives` llega con el `/auth/me`. Sin la espera, el primer render no
 * tiene permisos todavía y redirigiría a todo el mundo, incluido quien sí tiene
 * acceso: se vería como un parpadeo al dashboard al recargar la página.
 */
const RequirePermission: React.FC<{
  /** Todos los de la lista son obligatorios, igual que `authorize` en el backend. */
  permissions: string[];
  /** A dónde mandar a quien no cumple. */
  redirectTo?: string;
  children: React.ReactNode;
}> = ({ permissions, redirectTo = '/dashboard', children }) => {
  const { user, isAuthenticated } = useAuth();

  // Sesión todavía sin resolver: no se decide nada.
  if (!isAuthenticated || !user) return null;

  const allowed = permissions.every((p) => hasPermission(user.permissionsActives, p));
  if (!allowed) return <Navigate to={redirectTo} replace />;

  return <>{children}</>;
};

export default RequirePermission;
